import { Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { PromptService } from '../../components/prompt/prompt.service';
import { ReminderSeriesApiProviderService } from '../../data-providers/api-providers/reminder-series-api-provider/reminder-series-api-provider.service';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { EIntercomEvent } from '../../services/intercom-service-consts';
import { IntercomService } from '../../services/intercom.service';
import { ResponseErrorService } from '../errors/response-error.service';
import {
  EReminderSeriesFrequency,
  TReminderDTO,
  TReminderSeries,
  TReminderSeriesNewReminderPayload,
  TReminderSeriesUpdatePayload,
  TReminderSeriesUpgradeReminderPayload,
  TReminderUpdateDTO,
} from './reminders.consts';

@Injectable({
  providedIn: 'root',
})
export class RecurringRemindersService {
  private recurringReminders: {
    [reminderId: string]: TReminderSeries;
  } = {};

  constructor(
    private reminderSeriesApiProviderService: ReminderSeriesApiProviderService,
    private responseErrorService: ResponseErrorService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
    private intercomService: IntercomService,
  ) {}

  createRecurringReminder(
    newReminder: TReminderDTO,
    frequency: EReminderSeriesFrequency,
    endDate?: number,
  ): Observable<TReminderSeries> {
    const body: TReminderSeriesNewReminderPayload = {
      name: newReminder.name,
      description: newReminder.description,
      startDateEpochMillis: newReminder.timestampEpochMillis,
      pointId: newReminder.pointId,
      endDateEpochMillis: endDate,
      frequency,
      assignees: newReminder.assignees,
    };

    return this.reminderSeriesApiProviderService.createRecurringReminder(body).pipe(
      tap((reminderSeries) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_created');
        this.promptService.showSuccess(prompt);

        this.recurringReminders[reminderSeries.id] = reminderSeries;

        this.intercomService.trackIntercomEvent(EIntercomEvent.CREATE_REMINDER);
        this.intercomService.trackIntercomEvent(EIntercomEvent.CREATE_RECURRING_REMINDER);

        this.intercomService.markUserCreatedReminder();
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_creation_failed');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(
          error,
          'Failed to create recurring reminder',
        );
      }),
    );
  }

  updateReminderToRecurring(
    reminderId: string,
    frequency: EReminderSeriesFrequency,
    startDateEpochMillis: number,
    endDate?: number,
  ): Observable<TReminderSeries> {
    const body: TReminderSeriesUpgradeReminderPayload = {
      reminderId: reminderId,
      frequency,
      startDateEpochMillis: startDateEpochMillis,
    };

    if (endDate) {
      body.endDateEpochMillis = endDate;
    }

    return this.reminderSeriesApiProviderService.createRecurringReminder(body).pipe(
      tap((reminderSeries) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_upgraded');
        this.promptService.showSuccess(prompt);

        this.recurringReminders[reminderSeries.id] = reminderSeries;
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_upgrade_failed');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(
          error,
          'Failed to upgrade recurring reminder',
        );
      }),
    );
  }

  fetchRecurringReminders(seriesId: string): Observable<TReminderSeries> {
    if (this.recurringReminders[seriesId]) {
      return of(this.recurringReminders[seriesId]);
    }

    return this.reminderSeriesApiProviderService.getReminderSeries(seriesId).pipe(
      tap((reminderSeries) => {
        this.recurringReminders[reminderSeries.id] = reminderSeries;
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_fetch_failed');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(
          error,
          'Failed to fetch recurring reminder',
        );
      }),
    );
  }

  editRecurringReminder({
    reminderId,
    updatedReminder,
    frequency,
    seriesId,
    endDateEpochMillis,
    startDateEpochMillis,
  }: {
    reminderId: string;
    updatedReminder: TReminderUpdateDTO;
    frequency: EReminderSeriesFrequency;
    seriesId: string;
    startDateEpochMillis: number;
    endDateEpochMillis?: number;
  }): Observable<TReminderSeries> {
    const body: TReminderSeriesUpdatePayload = {
      reminderId,
      description: updatedReminder.description,
      name: updatedReminder.name,
      pointId: updatedReminder.pointId,
      endDateEpochMillis: endDateEpochMillis,
      frequency: frequency ? frequency : ('NONE' as EReminderSeriesFrequency),
      timestampEpochMillis: updatedReminder.timestampEpochMillis,
      startDateEpochMillis: startDateEpochMillis,
      assignees: updatedReminder.assignees,
    };

    return this.reminderSeriesApiProviderService.editReminderSeries(seriesId, body).pipe(
      tap((reminderSeries) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_updated');
        this.promptService.showSuccess(prompt);

        this.recurringReminders[reminderSeries.id] = reminderSeries;
      }),
      catchError((error) => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_update_failed');
        this.promptService.showError(prompt);

        return this.responseErrorService.handleRequestError(
          error,
          'Failed to update recurring reminder',
        );
      }),
    );
  }

  deleteRecurringReminder(seriesId: string, reminderId?: string): Observable<void> {
    return this.reminderSeriesApiProviderService.deleteReminderSeries(seriesId, reminderId).pipe(
      tap(() => {
        const prompt = this.translationPipe.transform('prompt_recurring_reminder_deleted');
        this.promptService.showSuccess(prompt);
        if (!reminderId) {
          delete this.recurringReminders[seriesId];
        }
      }),
      catchError((error) => {
        let prompt = this.translationPipe.transform('prompt_recurring_reminder_deletion_failed');

        switch (error.status) {
          case EStatusCode.NOT_FOUND:
            prompt = this.translationPipe.transform('prompt_recurring_reminder_not_found');
            break;
          case EStatusCode.FORBIDDEN:
            prompt = this.translationPipe.transform('prompt_recurring_reminder_forbidden');
            break;
        }

        this.promptService.showError(prompt);
        return this.responseErrorService.handleRequestError(
          error,
          'Failed to delete recurring reminder',
        );
      }),
    );
  }

  clearData(): void {
    this.recurringReminders = {};
  }
}
